import { useRef, useState } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import S3Image from 'Components/S3Image'
import { DocumentCard } from './DocumentCard'
import { NewDocumentForm } from './NewDocumentForm'
import { colors, outlinedPill } from './styles'
import { ACCEPTED_FILE_TYPES, isSatisfied, type AddDocumentHandler, type DocumentPerson, type DocumentRequirement } from './types'

interface DocumentRequirementCardProps {
  requirement: DocumentRequirement
  child: DocumentPerson
  canAdd: boolean
  onAdd: AddDocumentHandler
  onOpenDocument?: (documentID: number) => void
}

export function DocumentRequirementCard (props: DocumentRequirementCardProps): JSX.Element {
  const { requirement } = props
  const [expanded, setExpanded] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const uploadInput = useRef<HTMLInputElement>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  const satisfied = isSatisfied(requirement)
  const count = requirement.documents.length

  const selectFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selected = event.target.files?.[0] ?? null
    event.target.value = '' // allow re-selecting the same file after a cancel
    if (selected !== null) setFile(selected)
  }

  const include: AddDocumentHandler = async (pending) => {
    const errors = await props.onAdd(pending)
    if (errors.length === 0) setFile(null)
    return errors
  }

  const pickerButton = { ...outlinedPill, width: 201 }

  return <Box data-testid={`documentRequirement-${requirement.documentTypeID}`} sx={{ bgcolor: colors.cardBackground, borderRadius: '10px', overflow: 'hidden' }}>
    <Box
      onClick={() => { setExpanded(!expanded) }}
      role='button'
      aria-expanded={expanded}
      data-testid='documentRequirementHeader'
      sx={{ display: 'flex', alignItems: 'center', p: '20px', cursor: 'pointer' }}
    >
      <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
        {requirement.imageS3Key !== null && <S3Image imageKey={requirement.imageS3Key} alt='' style={{ width: '42px', height: '42px', objectFit: 'contain', flexShrink: 0 }} />}
        <Box sx={{ py: '4px', lineHeight: 1.3, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: colors.textPrimary }}>{requirement.name}</Typography>
          {requirement.description !== null && <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: colors.textTertiary }}>{requirement.description}</Typography>}
          <Typography data-testid='documentRequirementStatus' sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: satisfied ? colors.green : colors.red }}>
            {satisfied ? `${count} Document${count === 1 ? '' : 's'} Uploaded` : 'Required Document Missing'}
          </Typography>
        </Box>
      </Box>
      <IconButton aria-label={expanded ? 'Collapse' : 'Expand'} sx={{ color: colors.textPrimary, p: 0 }}>
        {expanded ? <Remove /> : <Add />}
      </IconButton>
    </Box>

    {expanded && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2, pb: '20px' }}>
      {requirement.documents.map(document => <DocumentCard key={document.documentID} document={document} onOpen={props.onOpenDocument} />)}

      {file !== null &&
        <NewDocumentForm requirement={requirement} child={props.child} file={file} onInclude={include} onCancel={() => { setFile(null) }} />}

      {props.canAdd && file === null && <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: '12px' }}>
        <input ref={uploadInput} hidden type='file' accept={ACCEPTED_FILE_TYPES} onChange={selectFile} data-testid='uploadFileInput' />
        <Button variant='outlined' sx={pickerButton} onClick={() => uploadInput.current?.click()} data-testid='uploadFile'>Upload a File</Button>
        {!satisfied && <>
          <Typography sx={{ fontSize: 12, color: '#1B1B1B' }}>OR</Typography>
          <input ref={photoInput} hidden type='file' accept='image/*' capture='environment' onChange={selectFile} data-testid='takePhotoInput' />
          <Button variant='outlined' sx={pickerButton} onClick={() => photoInput.current?.click()} data-testid='takePhoto'>Take Photo</Button>
        </>}
      </Box>}
    </Box>}
  </Box>
}
